/**
 * GET/PUT `/me/notifications/preferences` — the viewer's personal per-event
 * notification preferences (email / Slack, independent of org-wide alert
 * rules). Shared by `NotificationsSection` (org/personal settings page) and
 * `AutobuildPipelinePanel` (inline "notify me" toggle for a single event
 * kind), so both read/write through the same query key and stay in sync.
 *
 * @module features/notifications/hooks/useUserNotificationPreferences
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';

export type UserNotificationEventKind =
  | 'workflow_test_suite_completed'
  | 'qa_run_completed'
  | 'release_qa_completed'
  | 'flow_autobuild_completed';

export type UserNotificationPreference = {
  eventKind: UserNotificationEventKind;
  enabled: boolean;
  emailEnabled: boolean;
  slackEnabled: boolean;
};

export const USER_NOTIFICATION_PREFERENCES_QUERY_KEY = ['me-notification-preferences'] as const;

export function useUserNotificationPreferences(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: USER_NOTIFICATION_PREFERENCES_QUERY_KEY,
    enabled: opts?.enabled ?? true,
    queryFn: async (): Promise<{ ok: boolean; data: UserNotificationPreference[] }> => {
      const res = await fetch(`${API_URL}/me/notifications/preferences`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load personal notification preferences');
      return res.json();
    },
  });
}

export function useSaveUserNotificationPreference(opts?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      eventKind,
      pref,
    }: {
      eventKind: UserNotificationEventKind;
      pref: { enabled: boolean; emailEnabled: boolean; slackEnabled: boolean };
    }) => {
      const res = await fetch(`${API_URL}/me/notifications/preferences/${eventKind}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(pref),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to save personal notification preference');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USER_NOTIFICATION_PREFERENCES_QUERY_KEY });
      opts?.onSuccess?.();
    },
    onError: (error: Error) => opts?.onError?.(error),
  });
}
